/**
 * Outdoor decoration pixel data — trees, benches, bushes, flowers.
 *
 * Uses FURNITURE_PALETTE from palette.ts:
 *   0 = transparent
 *   1 = desk surface (wood brown)
 *   2 = desk shadow (dark brown)
 *   3 = desk highlight (light brown)
 *   4 = outline
 *   5 = metal
 *   6 = chair grey
 *   7 = white
 *   8 = plant green
 *   9 = plant dark
 *  10 = pot brown
 *  11 = pot shadow
 *  12 = dark surface
 *  13 = light surface
 */

import type { SpriteDefinition } from "../SpriteSheet.js";

// Helpers
function px(pixels: number[], w: number, x: number, y: number, color: number): void {
  if (x >= 0 && x < w && y >= 0 && y < (pixels.length / w)) {
    pixels[y * w + x] = color;
  }
}

function rect(pixels: number[], w: number, x: number, y: number, rw: number, rh: number, color: number): void {
  for (let dy = 0; dy < rh; dy++) {
    for (let dx = 0; dx < rw; dx++) {
      px(pixels, w, x + dx, y + dy, color);
    }
  }
}

// ==================================================================
// TREE — deciduous tree for outdoor campus (24x36)
// ==================================================================

function makeTree(): number[] {
  const W = 24, H = 36;
  const pixels = new Array<number>(W * H).fill(0);

  // Trunk
  rect(pixels, W, 10, 24, 4, 12, 10); // brown trunk
  rect(pixels, W, 11, 26, 2, 8, 11);  // trunk shadow

  // Canopy (layered circles for organic shape)
  // Bottom layer (widest)
  rect(pixels, W, 3, 12, 18, 10, 8);
  rect(pixels, W, 5, 11, 14, 1, 8);
  rect(pixels, W, 5, 22, 14, 1, 8);

  // Middle layer
  rect(pixels, W, 5, 8, 14, 6, 8);
  rect(pixels, W, 7, 7, 10, 1, 8);

  // Top layer
  rect(pixels, W, 7, 4, 10, 4, 8);
  rect(pixels, W, 9, 3, 6, 1, 8);

  // Dark leaf clusters (depth/shadow)
  rect(pixels, W, 5, 14, 6, 4, 9);
  rect(pixels, W, 14, 16, 5, 3, 9);
  rect(pixels, W, 8, 8, 4, 3, 9);
  rect(pixels, W, 13, 10, 3, 3, 9);

  // Highlight patches (sunlit leaves)
  rect(pixels, W, 9, 5, 3, 2, 8);
  rect(pixels, W, 15, 12, 3, 2, 8);

  // Outline hint on left/right edges
  for (let y = 12; y <= 21; y++) {
    px(pixels, W, 2, y, 9);
    px(pixels, W, 21, y, 9);
  }

  return pixels;
}

export const OUTDOOR_TREE: SpriteDefinition = {
  name: "tree",
  frames: [{ width: 24, height: 36, pixels: makeTree() }],
  originX: 12,
  originY: 32,
};

// ==================================================================
// BENCH — park bench for outdoor seating (32x16)
// ==================================================================

function makeBench(): number[] {
  const W = 32, H = 16;
  const pixels = new Array<number>(W * H).fill(0);

  // Seat planks (3 wooden slats)
  rect(pixels, W, 2, 4, 28, 2, 1);  // back plank
  rect(pixels, W, 2, 4, 28, 1, 3);  // highlight
  rect(pixels, W, 2, 7, 28, 2, 1);  // middle plank
  rect(pixels, W, 2, 7, 28, 1, 3);  // highlight
  rect(pixels, W, 2, 10, 28, 2, 1); // front plank
  rect(pixels, W, 2, 10, 28, 1, 3); // highlight

  // Metal legs
  rect(pixels, W, 4, 6, 2, 10, 5);
  rect(pixels, W, 26, 6, 2, 10, 5);

  // Metal arm rests
  rect(pixels, W, 4, 2, 2, 3, 5);
  rect(pixels, W, 26, 2, 2, 3, 5);

  // Feet
  rect(pixels, W, 3, 14, 4, 2, 6);
  rect(pixels, W, 25, 14, 4, 2, 6);

  return pixels;
}

export const OUTDOOR_BENCH: SpriteDefinition = {
  name: "bench",
  frames: [{ width: 32, height: 16, pixels: makeBench() }],
  originX: 16,
  originY: 12,
};

// ==================================================================
// BUSH — low hedge/bush for garden decoration (16x12)
// ==================================================================

function makeBush(): number[] {
  const W = 16, H = 12;
  const pixels = new Array<number>(W * H).fill(0);

  // Main bush body (rounded blob)
  rect(pixels, W, 2, 4, 12, 6, 8);
  rect(pixels, W, 4, 3, 8, 1, 8);
  rect(pixels, W, 4, 10, 8, 1, 8);
  rect(pixels, W, 1, 5, 1, 4, 8);
  rect(pixels, W, 14, 5, 1, 4, 8);

  // Dark patches
  rect(pixels, W, 3, 6, 4, 3, 9);
  rect(pixels, W, 10, 5, 3, 2, 9);

  // Ground shadow
  rect(pixels, W, 3, 10, 10, 2, 9);

  return pixels;
}

export const OUTDOOR_BUSH: SpriteDefinition = {
  name: "bush",
  frames: [{ width: 16, height: 12, pixels: makeBush() }],
  originX: 8,
  originY: 10,
};

// ==================================================================
// FLOWER — small flower cluster for garden detail (12x14)
// ==================================================================

function makeFlower(): number[] {
  const W = 12, H = 14;
  const pixels = new Array<number>(W * H).fill(0);

  // Stems
  px(pixels, W, 3, 8, 9);
  px(pixels, W, 3, 9, 9);
  px(pixels, W, 3, 10, 9);
  px(pixels, W, 6, 7, 9);
  px(pixels, W, 6, 8, 9);
  px(pixels, W, 6, 9, 9);
  px(pixels, W, 6, 10, 9);
  px(pixels, W, 9, 8, 9);
  px(pixels, W, 9, 9, 9);
  px(pixels, W, 9, 10, 9);

  // Small leaves on stems
  px(pixels, W, 2, 9, 8);
  px(pixels, W, 4, 9, 8);
  px(pixels, W, 5, 8, 8);
  px(pixels, W, 7, 8, 8);
  px(pixels, W, 8, 9, 8);
  px(pixels, W, 10, 9, 8);

  // Flower heads (colored dots — using palette creatively)
  // Left flower (white)
  rect(pixels, W, 2, 5, 3, 3, 7);
  px(pixels, W, 3, 6, 13); // center

  // Middle flower (light)
  rect(pixels, W, 5, 4, 3, 3, 7);
  px(pixels, W, 6, 5, 13); // center

  // Right flower (white)
  rect(pixels, W, 8, 5, 3, 3, 7);
  px(pixels, W, 9, 6, 13); // center

  // Ground dirt patch
  rect(pixels, W, 1, 11, 10, 3, 11);
  rect(pixels, W, 2, 11, 8, 1, 10);

  return pixels;
}

export const OUTDOOR_FLOWER: SpriteDefinition = {
  name: "flower",
  frames: [{ width: 12, height: 14, pixels: makeFlower() }],
  originX: 6,
  originY: 12,
};
